/**
 * CREATIVE DETONATOR - Art Director Refinement Module
 *
 * Provides headline navigation and card render helpers.
 * Note: Refinement is now handled via Idea Canvas (refine-form.js)
 *
 * Features:
 * - Headline navigation (arrows for cycling alternatives)
 * - Render helpers for cards (headline with nav, critique section, badge)
 */

import { getCurrentIdea } from '../core/state.js';
import { getIdeaImageUrl } from './image-generation.js';

// ============================================================================
// HEADLINE NAVIGATION
// ============================================================================

/**
 * Navigate between alternative headlines
 */
export function navigateHeadline(ideaId, direction) {
  const idea = getCurrentIdea(ideaId);
  if (!idea || !idea.alternativeHeadlines) return;

  const totalOptions = idea.alternativeHeadlines.length + 1; // +1 for main
  let currentIndex = idea.currentHeadlineIndex || 0;

  // Calculate new index with wrap-around
  currentIndex = (currentIndex + direction + totalOptions) % totalOptions;

  // Update state
  idea.currentHeadlineIndex = currentIndex;

  // Update display
  updateHeadlineDisplay(ideaId, idea, currentIndex);
}

/**
 * Update headline display in card
 */
function updateHeadlineDisplay(ideaId, idea, index) {
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) return;

  const headlineEl = card.querySelector('.idea-card-headline');
  const sublineSection = card.querySelector('.idea-card-subline-section');
  const counterEl = card.querySelector('.headline-counter');

  let headline, subline;

  if (index === 0) {
    headline = idea.headline;
    subline = idea.subline;
  } else {
    const alt = idea.alternativeHeadlines[index - 1];
    headline = alt.headline || alt.super || alt.hook || alt.cta || alt.campaignLine || alt.reveal;
    subline = alt.subline || null;
  }

  if (headlineEl) {
    headlineEl.textContent = `"${headline}"`;
  }

  if (sublineSection) {
    if (subline) {
      sublineSection.style.display = '';
      const sublineEl = sublineSection.querySelector('.idea-card-subline');
      if (sublineEl) sublineEl.textContent = subline;
    } else {
      sublineSection.style.display = 'none';
    }
  }

  if (counterEl) {
    const total = idea.alternativeHeadlines.length + 1;
    counterEl.textContent = `${index + 1}/${total}`;
  }
}

// ============================================================================
// CLICK HANDLER FOR HEADLINE NAVIGATION
// ============================================================================

/**
 * Handle clicks on headline navigation arrows
 * @param {Event} e - Click event
 */
function handleHeadlineNavClick(e) {
  // Headline prev arrow
  const prevBtn = e.target.closest('.headline-nav-prev');
  if (prevBtn) {
    e.preventDefault();
    e.stopPropagation();
    navigateHeadline(prevBtn.dataset.ideaId, -1);
    return;
  }

  // Headline next arrow
  const nextBtn = e.target.closest('.headline-nav-next');
  if (nextBtn) {
    e.preventDefault();
    e.stopPropagation();
    navigateHeadline(nextBtn.dataset.ideaId, 1);
    return;
  }
}

// Initialize headline navigation on document body (event delegation)
if (typeof document !== 'undefined') {
  document.body.addEventListener('click', handleHeadlineNavClick);
}

// ============================================================================
// RENDER HELPERS (for idea-card.js)
// ============================================================================

/**
 * Render headline section with navigation (for refined ideas with alternatives)
 */
export function renderHeadlineWithNav(idea, ideaId) {
  const hasAlternatives = idea.alternativeHeadlines && idea.alternativeHeadlines.length > 0;
  const label = idea.headlineLabel || 'HEADLINE';

  if (!hasAlternatives) {
    return `
      <div class="idea-card-section idea-card-headline-section">
        <span class="idea-card-label">${label}</span>
        <p class="idea-card-headline">"${idea.headline}"</p>
      </div>
    `;
  }

  return `
    <div class="idea-card-section idea-card-headline-section">
      <span class="idea-card-label">${label}</span>
      <div class="headline-nav-wrapper">
        <button class="headline-nav-btn headline-nav-prev" data-idea-id="${ideaId}">&lt;</button>
        <p class="idea-card-headline">"${idea.headline}"</p>
        <button class="headline-nav-btn headline-nav-next" data-idea-id="${ideaId}">&gt;</button>
      </div>
      <span class="headline-counter">1/${idea.alternativeHeadlines.length + 1}</span>
    </div>
  `;
}

/**
 * Render critique section (for refined ideas)
 */
export function renderCritiqueSection(idea) {
  if (!idea.critique) return '';

  return `
    <div class="idea-card-section idea-card-critique-section">
      <span class="idea-card-label critique-label">WHAT WAS FIXED</span>
      <p class="idea-card-critique">${idea.critique}</p>
    </div>
  `;
}

/**
 * Render refined badge for card meta
 */
export function renderRefinedBadge(idea) {
  if (!idea.isRefined) return '';

  const text = idea.refinementCount > 1 ? `REFINED x${idea.refinementCount}` : 'REFINED';
  return `<span class="refined-badge">${text}</span>`;
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for compatibility
window.navigateHeadline = navigateHeadline;
